import { MutationFunction, QueryKey, useMutation } from '@tanstack/react-query';
import { queryClient } from '../../services/queryClient';
import {
    useDispatchErrorNotification,
    useDispatchSuccessfullySavedRecord,
    useDispatchSuccessfullyDeletedRecord,
} from '..';

interface useCreateRecordProps<TData = unknown, TError = unknown, TVariables = void, TContext = unknown> {
    createRecordFn: MutationFunction<TData, TVariables>;
    fetchRecordsQueryKey: QueryKey;
    recordName: string;
    onSuccess?: () => void;
    onSettled?: (
        data: TData | undefined,
        error: TError | null,
        variables: TVariables,
        context: TContext | undefined
    ) => void;
}

export function useCreateRecord<TData, TError extends Error, TVariables = void, TContext = unknown>({
    createRecordFn,
    fetchRecordsQueryKey,
    recordName,
    onSuccess,
    onSettled,
}: useCreateRecordProps<TData, TError, TVariables, TContext>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<TData, TError, TVariables, TContext>({
        mutationFn: createRecordFn,
        onSuccess: async (createdRecord) => {
            if (onSuccess) onSuccess();
            else {
                await queryClient.cancelQueries({ queryKey: fetchRecordsQueryKey });
                queryClient.setQueryData(fetchRecordsQueryKey, (prevCachedRecords: any) => {
                    return [...(prevCachedRecords || []), { ...createdRecord, isNewRecord: false }];
                });
            }

            dispatchSuccessfullySavedRecord(recordName);
        },
        onError: (error: TError) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: (data, error, variables, context) => {
            if (onSettled) onSettled(data, error, variables, context);
        },
    });
}

interface useUpdateRecordProps<TData = unknown, TError = unknown, TVariables = void, TContext = unknown> {
    updateRecordFn: MutationFunction<TData, TVariables>;
    fetchRecordsQueryKey: QueryKey;
    recordName: string;
    onSettled?: (
        data: TData | undefined,
        error: TError | null,
        variables: TVariables,
        context: TContext | undefined
    ) => void;
    /** Map a server error onto a user-facing message. Without it the raw `error.message` is toasted,
     *  which for a handled status (a 409 conflict, say) means English prose in a German UI. */
    errorMessage?: (error: TError) => string;
}

export function useUpdateRecord<TData, TError extends Error, TVariables, TContext = unknown>({
    updateRecordFn,
    fetchRecordsQueryKey,
    recordName,
    onSettled,
    errorMessage,
}: useUpdateRecordProps<TData, TError, TVariables, TContext>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<TData, TError, TVariables, TContext>({
        mutationFn: updateRecordFn,
        onSuccess: () => {
            dispatchSuccessfullySavedRecord(recordName);
        },
        onError: (error) => {
            dispatchErrorNotification({ message: errorMessage ? errorMessage(error) : error.message });
        },
        onSettled: (data, error, variables, context) => {
            if (onSettled) onSettled(data, error, variables, context);

            queryClient.invalidateQueries(
                Array.isArray(fetchRecordsQueryKey) ? fetchRecordsQueryKey : [fetchRecordsQueryKey]
            );
        },
    });
}

interface useDeleteRecordProps<TData = unknown, TError = unknown, TVariables = void, TContext = unknown> {
    deleteRecordFn: MutationFunction<TData, TVariables>;
    fetchRecordsQueryKey: QueryKey;
    recordName: string;
    onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
    onSettled?: (
        data: TData | undefined,
        error: TError | null,
        variables: TVariables,
        context: TContext | undefined
    ) => void;
}

export function useDeleteRecord<TData, TError extends Error, TVariables, TContext = unknown>({
    deleteRecordFn,
    fetchRecordsQueryKey,
    recordName,
    onSuccess,
    onSettled,
}: useDeleteRecordProps<TData, TError, TVariables, TContext>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullyDeletedRecord } = useDispatchSuccessfullyDeletedRecord();

    return useMutation<TData, TError, TVariables, TContext>({
        mutationFn: deleteRecordFn,
        onSuccess: (data, variables, context) => {
            if (onSuccess) onSuccess(data, variables, context);
            dispatchSuccessfullyDeletedRecord(recordName);
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: (data, error, variables, context) => {
            if (onSettled) onSettled(data, error, variables, context);
            queryClient.invalidateQueries(fetchRecordsQueryKey);
        },
    });
}
